import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import globeIcon from '@/features/Company/icons/globe-icon.png'
import markerIcon from '@/features/Company/icons/marker-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import Head from '@/components/head'

import concertRealtyQR from './concert-properties-qr.png'
import heroBanner from './concert-realty-banner.png'

const ConcertRealtyServicesProfile = ({
  metadata,
  footerOnScreen,
  listingsOnScreen
}) => {
  const { t } = useTranslation('concertrealtyservices')
  const profile = {
    banner: heroBanner.src,
    title: 'Concert Realty Services',
    url: '/company/concertrealtyservices'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
    desktop: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '500px' : '350px' }}
        marginBottom="medium"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <Image
            fill
            priority
            src={heroBanner.src}
            alt="Concert Realty Banner"
            style={{
              objectFit: 'cover',
              objectPosition: '50% 50%'
            }}
            sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
          />
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection={{ mobile: 'column', tablet: 'row' }}
        gap="gutter"
        justifyContent="center"
        marginY="large"
      >
        <Stack space="medium">
          <Box
            display="flex"
            alignItems="center"
            marginRight="large"
            marginLeft="medium"
          >
            <Heading level="3" weight="weak">
              {t('concertrealtyservices:Concert Realty Services Ltd.')}
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom={{
              mobile: 'medium',
              tablet: 'medium',
              desktop: 'xlarge'
            }}
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {t('concertrealtyservices:subtitle')}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Stack>
        <Box
          display="flex"
          flexDirection="column"
          gap="medium"
          background="neutralSoft"
          borderRadius="small"
          paddingY="small"
          paddingX="gutter"
          marginX="medium"
          style={{
            height: 'min-content',
            minWidth: '320px'
          }}
        >
          <Box display="flex" alignItems="center" width="full">
            <Box marginRight="xxsmall">
              <Image src={globeIcon.src} alt="" width={16} height={16} />
            </Box>
            <TextLink
              href="https://www.concertproperties.com/rentals"
              rel="noreferrer noopener"
              target="_blank"
            >
              www.concertproperties.com/rentals
            </TextLink>
          </Box>
          <Box display="flex" alignItems="center" width="full">
            <Box marginRight="xxsmall">
              <Image src={phoneIcon.src} alt="" width={16} height={16} />
            </Box>
            <TextLink href={`tel: 604 688 9460`} rel="noreferrer noopener">
              604.688.9460
            </TextLink>
          </Box>
          <Box display="flex" alignItems="center" width="full">
            <Box marginRight="xxsmall">
              <Image src={markerIcon.src} alt="" width={16} height={16} />
            </Box>
            <TextLink
              target="_blank"
              href="https://maps.app.goo.gl/HaPDBphhpKhTqi7UA"
              rel="noreferrer noopener"
            >
              1190 Hornby Street, Vancouver, BC
            </TextLink>
          </Box>
        </Box>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('concertrealtyservices:Our Portfolio')}</Heading>
      </Box>
      {isDesktop && (
        <Box
          position={footerOnScreen && listingsOnScreen ? 'absolute' : 'fixed'}
          zIndex="dropdown"
          border="borderNeutralLight"
          background="bodyWhite"
          display={{ mobile: 'none', tablet: 'none', desktop: 'flex' }}
          style={{
            top: !footerOnScreen && !listingsOnScreen ? '1000px' : null,
            bottom: '20px',
            right: '10px'
          }}
          paddingBottom="xsmall"
        >
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            justifyContent="center"
            gap="xxsmall"
            style={{ width: '158px', height: '175px' }}
          >
            <Image
              src={concertRealtyQR}
              alt="Concert Realty QR Code"
              width={160}
              height={160}
              placeholder="blur"
            />
            <Text align="center" size="xsmall" weight="medium">
              {t('concertrealtyservices:QR description')}
            </Text>
          </Box>
        </Box>
      )}
    </>
  )
}

export default ConcertRealtyServicesProfile
